import { LitElement, css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import '@shoelace-style/shoelace/dist/components/dialog/dialog.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import { styles as sharedStyles } from '../styles/shared-styles';
import {
  pendingSalesStore,
  type PendingSalesSnapshot,
} from '../state/pending-sales-store';
import type { PendingSale } from '../database/sale-outbox';

// Q.3 follow-up — review + resolve offline sales the SERVER rejected on replay.
//
// A queued offline sale normally drains itself when the network returns. But if
// the server rejects the replay (the menu item was deleted, the trading day or
// shift closed while it sat queued, a validation rule changed), the drainer
// parks it as `failed` and skips it forever. Without this surface the operator
// is stuck with a sale that was shown as "paid" at the till but never recorded,
// and no way to see why or clear it.
//
// This dialog lists each failed sale with the server's reason and two actions:
//  - Retry: the operator fixed the cause (reopened the day, restored the item)
//    and wants it to land. The idempotency key is preserved, so a retry that
//    actually already landed returns the existing sale rather than duplicating.
//  - Discard: the operator accepts the sale will NOT be recorded (real money —
//    confirmed inline; they may need to re-ring it).
//
// `open` is bound by the parent (app-shell); we emit `close` to request close.
@customElement('pending-sales-review')
export class PendingSalesReview extends LitElement {
  static styles = [
    sharedStyles,
    css`
      :host {
        display: block;
      }

      .intro {
        color: var(--sl-color-neutral-700);
        font-size: 0.9rem;
        margin: 0 0 0.75rem;
      }

      .empty {
        color: var(--sl-color-neutral-600);
        text-align: center;
        padding: 1.5rem 0.5rem;
      }

      .entry {
        border: 1px solid var(--sl-color-neutral-200);
        border-radius: var(--sl-border-radius-medium);
        padding: 0.7rem 0.85rem;
        margin-bottom: 0.7rem;
      }

      .entry__head {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
        gap: 0.5rem;
        margin-bottom: 0.35rem;
      }

      .entry__tenders {
        font-weight: 600;
        text-transform: capitalize;
      }

      .entry__time {
        color: var(--sl-color-neutral-600);
        font-size: 0.8rem;
        white-space: nowrap;
      }

      .entry__lines {
        margin: 0 0 0.5rem;
        padding-left: 1.1rem;
        color: var(--sl-color-neutral-800);
        font-size: 0.85rem;
      }

      .entry__lines li {
        font-variant-numeric: tabular-nums;
      }

      .entry__error {
        margin-bottom: 0.6rem;
      }

      .entry__actions {
        display: flex;
        gap: 0.5rem;
        justify-content: flex-end;
        align-items: center;
        flex-wrap: wrap;
      }

      .confirm {
        color: var(--sl-color-danger-700);
        font-size: 0.85rem;
        margin-right: auto;
      }
    `,
  ];

  // Bound by the parent so a single instance can be reused.
  @property({ type: Boolean, reflect: true }) open = false;

  @state() private _failed: PendingSale[] = pendingSalesStore.snapshot.failed;
  // The entry currently awaiting a discard confirmation (its idempotency key),
  // or null. Discard is real money, so it takes a second deliberate tap.
  @state() private _confirmingDiscard: string | null = null;
  // Disable buttons while a retry/discard round-trips to IDB + the drainer.
  @state() private _busyKey: string | null = null;

  private _unsub?: () => void;

  connectedCallback(): void {
    super.connectedCallback();
    this._unsub = pendingSalesStore.subscribe((snap: PendingSalesSnapshot) => {
      this._failed = snap.failed;
      // If the list a confirm was targeting drained away, drop the prompt.
      if (
        this._confirmingDiscard &&
        !snap.failed.some((s) => s.idempotencyKey === this._confirmingDiscard)
      ) {
        this._confirmingDiscard = null;
      }
    });
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this._unsub?.();
    this._unsub = undefined;
  }

  private _close(): void {
    this._confirmingDiscard = null;
    this.dispatchEvent(
      new CustomEvent('close', { bubbles: true, composed: true })
    );
  }

  private async _retry(key: string): Promise<void> {
    this._busyKey = key;
    try {
      await pendingSalesStore.retryFailed(key);
    } finally {
      this._busyKey = null;
    }
  }

  private async _discard(key: string): Promise<void> {
    this._busyKey = key;
    try {
      await pendingSalesStore.discardFailed(key);
      this._confirmingDiscard = null;
    } finally {
      this._busyKey = null;
    }
  }

  private _tenderSummary(sale: PendingSale): string {
    const methods = sale.payload.tenders?.map((t) => t.method).filter(Boolean);
    return methods && methods.length ? methods.join(' + ') : 'sale';
  }

  private _queuedAt(sale: PendingSale): string {
    const d = new Date(sale.queuedAtUtc);
    if (Number.isNaN(d.getTime())) return sale.queuedAtUtc;
    return d.toLocaleString();
  }

  private _renderEntry(sale: PendingSale) {
    const key = sale.idempotencyKey;
    const busy = this._busyKey === key;
    const confirming = this._confirmingDiscard === key;
    const lines = sale.payload.lines ?? [];
    return html`
      <div class="entry">
        <div class="entry__head">
          <span class="entry__tenders">${this._tenderSummary(sale)}</span>
          <span class="entry__time">${this._queuedAt(sale)}</span>
        </div>
        <ul class="entry__lines">
          ${lines.map((l) => html`<li>${l.quantity} × ${l.productId}</li>`)}
        </ul>
        <sl-alert class="entry__error" variant="danger" open>
          <sl-icon slot="icon" name="exclamation-octagon"></sl-icon>
          ${sale.lastError ?? 'The server rejected this sale.'}
        </sl-alert>
        <div class="entry__actions">
          ${
            confirming
              ? html`
                  <span class="confirm">
                    Discard this sale? It was never recorded.
                  </span>
                  <sl-button
                    size="small"
                    variant="text"
                    ?disabled=${busy}
                    @click=${() => (this._confirmingDiscard = null)}
                    >Keep</sl-button
                  >
                  <sl-button
                    size="small"
                    variant="danger"
                    ?loading=${busy}
                    @click=${() => this._discard(key)}
                    >Discard</sl-button
                  >
                `
              : html`
                  <sl-button
                    size="small"
                    variant="danger"
                    outline
                    ?disabled=${busy}
                    @click=${() => (this._confirmingDiscard = key)}
                    >Discard</sl-button
                  >
                  <sl-button
                    size="small"
                    variant="primary"
                    ?loading=${busy}
                    @click=${() => this._retry(key)}
                    >Retry</sl-button
                  >
                `
          }
        </div>
      </div>
    `;
  }

  render() {
    return html`
      <sl-dialog
        class="full-page-on-phone"
        label="Sales that need attention"
        ?open=${this.open}
        @sl-request-close=${() => this._close()}
      >
        ${
          this._failed.length === 0
            ? html`<div class="empty">No sales need attention.</div>`
            : html`
                <p class="intro">
                  These offline sales were rejected when they tried to sync. Fix
                  the cause and <strong>Retry</strong>, or
                  <strong>Discard</strong>
                  if the sale should not be recorded.
                </p>
                ${this._failed.map((s) => this._renderEntry(s))}
              `
        }
        <sl-button
          slot="footer"
          variant="primary"
          @click=${() => this._close()}
        >
          Done
        </sl-button>
      </sl-dialog>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'pending-sales-review': PendingSalesReview;
  }
}
